import {
  useCallback, useState,
} from 'react';
import { CONFIG } from '~/_shared/constants/config';
import { GAEventName } from '~/_shared/utils/3rdPartyIntegrations/ga/ga.types';
import {
  sendSignupFunnelEvent, type SignupFunnelPage, storeSsoOriginPage,
} from '~/_shared/utils/3rdPartyIntegrations/ga/signupFunnel.helpers';
import {
  generateCodeChallenge, generateCodeVerifier, generateSecureString,
} from '~/_shared/utils/crypto';
import {
  GOOGLE_SECURITY_TOKEN_ITEM_KEY, GOOGLE_SECURITY_TOKEN_KEY, GOOGLE_SSO_PROMPT, GOOGLE_SSO_SCOPES,
} from './googleSsoPage.container';
import {
  MS_PKCE_CODE_VERIFIER_KEY,
  MS_SECURITY_TOKEN_ITEM_KEY, MS_SECURITY_TOKEN_KEY,
  MS_SSO_SCOPES,
} from './msSsoPage.container';
import {
  getGoogleSsoRedirectUri, getMsSsoRedirectUri,
} from './sso.helpers';

export const useSsoLogin = (page: SignupFunnelPage) => {
  const [isGeneratingCodeChallenge, setIsGeneratingCodeChallenge] = useState(false);

  const onGoogleLogin = useCallback(() => {
    // the security token is stored right before redirecting, so a repeated click can't leave a token that doesn't match the URL
    const redirectToGoogle = () => {
      const secureString = generateSecureString();
      const state = encodeURIComponent(JSON.stringify({ [GOOGLE_SECURITY_TOKEN_KEY]: secureString }));

      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${new URLSearchParams({
        client_id: CONFIG.GOOGLE_OAUTH_CLIENT_ID,
        redirect_uri: getGoogleSsoRedirectUri(),
        response_type: 'code',
        scope: GOOGLE_SSO_SCOPES,
        state,
        access_type: 'offline',
        prompt: GOOGLE_SSO_PROMPT,
      }).toString()}`;

      sessionStorage.setItem(GOOGLE_SECURITY_TOKEN_ITEM_KEY, secureString);
      storeSsoOriginPage(page);
      window.location.href = authUrl;
    };

    sendSignupFunnelEvent(GAEventName.SignupMethodClick, { page, method: 'google' }, redirectToGoogle);
  }, [page]);

  const onMicrosoftLogin = useCallback(async () => {
    const secureString = generateSecureString();
    const state = encodeURIComponent(JSON.stringify({ [MS_SECURITY_TOKEN_KEY]: secureString }));

    // PKCE implementation
    const codeVerifier = generateCodeVerifier();

    setIsGeneratingCodeChallenge(true);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    setIsGeneratingCodeChallenge(false);

    const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${new URLSearchParams({
      tenant: 'common',
      client_id: CONFIG.MS_OAUTH_CLIENT_ID || '',
      redirect_uri: getMsSsoRedirectUri(),
      response_type: 'code',
      scope: MS_SSO_SCOPES,
      state,
      prompt: 'consent',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256', // SHA-256 hashing method
    }).toString()}`;

    // the security token and code verifier are stored right before redirecting, so a repeated click can't leave values that don't match the URL
    const redirectToMicrosoft = () => {
      sessionStorage.setItem(MS_SECURITY_TOKEN_ITEM_KEY, secureString);
      // Store code verifier in sessionStorage to use during token exchange
      sessionStorage.setItem(MS_PKCE_CODE_VERIFIER_KEY, codeVerifier);
      storeSsoOriginPage(page);
      window.location.href = authUrl;
    };

    sendSignupFunnelEvent(GAEventName.SignupMethodClick, { page, method: 'microsoft' }, redirectToMicrosoft);
  }, [page]);

  return {
    isLoading: isGeneratingCodeChallenge,
    onGoogleLogin,
    onMicrosoftLogin,
  };
};
